import React, { useEffect, useMemo, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { paths } from 'routes/paths';
import EmptyTableState from 'components/EmptyTableState';
import LoadingSpinner from 'components/LoadingSpinner';
import PlusIcon from 'assets/plus.svg?react';
import NoInvoicesFoundIcon from 'assets/no_applications_found.svg';
import NoCropSeasonSelectedIcon from 'assets/no-crop-season-selected.svg';
import InvoicesRecordIcon from 'assets/InvoicesRecordIcon.svg?react';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { buildLandingPage } from 'screens/Applications&WorkOrders/Components/helpers';
import {
  SummaryParamsProvider,
  useSummaryParams
} from 'components/RecordsListView/context/SummaryParamsContext';
import { OperationSortProvider } from 'components/RecordsListView/context/SortContext';
import { OperationType } from 'components/RecordsListView/constants';
import {
  OperationSummaryParams,
  SortConfig,
  SORT_BY_VALUES,
  SORT_ORDER_VALUES
} from 'components/RecordsListView/types';
import { SortOption } from 'components/RecordsListView/SortDropdownMenu';
import useDestroyRecordSourceMessageOnNavigation from 'screens/Applications&WorkOrders/Components/OperationsPage/useDestroyRecordSourceMessageOnNavigation';
import useDismissNotificationOnNavigation from 'components/Notification/useDismissNotificationOnNavigation';
import { SEASON_IDS_PARAM } from 'utilities/cropSeasonUrl';
import useSelectionLoadLatch, {
  cropSeasonSelectionKey
} from 'hooks/useSelectionLoadLatch';
import useInvoicesSummary, {
  UseInvoicesSummaryReturn
} from './hooks/useInvoicesSummary';
import useInvoiceEvents from './hooks/useInvoiceEvents';
import InvoiceListView from './components/InvoiceListView';
import { dismissInvoiceNotification } from './components/Notification';

export const INVOICE_SORT_OPTIONS: SortOption[] = [
  {
    key: 'sort-asc-invoice-name',
    label: 'Invoice Name (A→Z)',
    sorts: [{ sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }]
  },
  {
    key: 'sort-desc-invoice-name',
    label: 'Invoice Name (Z→A)',
    sorts: [{ sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.DESC }]
  },
  {
    key: 'sort-desc-invoice-date',
    label: 'Invoice Date (New→Old)',
    sorts: [
      { sortBy: SORT_BY_VALUES.START_DATE, order: SORT_ORDER_VALUES.DESC }
    ]
  },
  {
    key: 'sort-asc-invoice-date',
    label: 'Invoice Date (Old→New)',
    sorts: [{ sortBy: SORT_BY_VALUES.START_DATE, order: SORT_ORDER_VALUES.ASC }]
  }
];

/**
 * FR-051: the Invoices landing list defaults to Invoice Date, newest first
 * (not the generic Applications/Work Orders `DEFAULT_SORT`, which sorts by
 * Creation Date — an option intentionally absent from `INVOICE_SORT_OPTIONS`).
 * Reuses the exact `sorts` array of the "Invoice Date (New→Old)" option so
 * `SortDropdownMenu` resolves the correct selected label on initial load.
 */
export const INVOICE_DEFAULT_SORT: SortConfig[] = INVOICE_SORT_OPTIONS[2].sorts;

export interface InvoicesPageConfig {
  icon: React.ReactNode;
  title: string;
  breadcrumbTitle: string;
  breadcrumbValue: string;
  searchPlaceholder: string;
  createButtonLabel: string;
  operationType: OperationType;
  createPath: string;
  storageKey: string;
  sortOptions: SortOption[];
}

export const InvoicesConfig: InvoicesPageConfig = {
  icon: <InvoicesRecordIcon />,
  title: 'Invoices',
  breadcrumbTitle: 'Invoices',
  breadcrumbValue: 'All Invoices',
  searchPlaceholder: 'Search invoices',
  createButtonLabel: 'Create New',
  operationType: OperationType.INVOICE,
  createPath: paths.invoices,
  storageKey: 'invoices',
  sortOptions: INVOICE_SORT_OPTIONS
};

type UseSummaryHook = (
  params?: OperationSummaryParams
) => UseInvoicesSummaryReturn;

interface InvoicesPageWrapperProps {
  config: InvoicesPageConfig;
  useSummaryHook: UseSummaryHook;
}

/**
 * Reads the URL-synced summary params, fetches the invoice summaries, and feeds
 * the smart `InvoiceListView` inside the shared landing-page chrome. Mirrors
 * `OperationsPageWrapper` but stays invoice-specific (invoices don't gate on
 * crop zones and use a native create route).
 */
export const InvoicesPageWrapper: React.FC<InvoicesPageWrapperProps> = ({
  config,
  useSummaryHook
}) => {
  const history = useHistory();
  const location = useLocation();
  const { params, hasSearchTerm, hasFilters } = useSummaryParams();
  const { sendInitiateCreateEvent } = useInvoiceEvents();

  // The season(s) carried from the product view (`?seasonIds`) are consumed at
  // load — a new tab reads them via applyUrlCropSeasons, a same-tab click seeds
  // them before navigating. Strip the param once here so a later refresh can't
  // re-apply it over the user's own season selection.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.has(SEASON_IDS_PARAM)) {
      return;
    }
    searchParams.delete(SEASON_IDS_PARAM);
    const nextSearch = searchParams.toString();
    // Rebuilt from explicit fields rather than spreading `location`: that would
    // carry `state` back into the history entry, undoing the one-time clear
    // `useDeepLinkSelection` performs on mount and letting a Back navigation
    // re-select the deep-linked invoice over the user's own choice.
    history.replace({
      pathname: location.pathname,
      hash: location.hash,
      search: nextSearch ? `?${nextSearch}` : ''
    });
  }, [location, history]);

  const [, forceRefetch] = useReducer((count: number) => count + 1, 0);

  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const hasSelectedCropSeasons = selectedCropSeasonIds.length > 0;
  const summaryParams = useMemo(
    () => ({ ...params, cropSeasonIds: selectedCropSeasonIds }),
    [params, selectedCropSeasonIds]
  );

  const {
    loading,
    invoices,
    totalElements,
    pageSize,
    currentPage,
    loadInvoices,
    errors,
    loadedCropSeasonIds
  } = useSummaryHook(summaryParams);

  // The summary fetch is suppressed while no crop season is selected, which
  // leaves the previous org's results in the hook's state after switching to an
  // org that has none. Drop them so the list reads empty rather than showing
  // another org's invoices.
  const items = useMemo(
    () => (loading || errors || !hasSelectedCropSeasons ? [] : invoices ?? []),
    [loading, errors, invoices, hasSelectedCropSeasons]
  );

  const hasCompletedInitialLoad = useSelectionLoadLatch({
    selectionKey: cropSeasonSelectionKey(selectedCropSeasonIds),
    loading,
    loadedDataKey: cropSeasonSelectionKey(loadedCropSeasonIds)
  });

  const createNewInvoice = () => {
    sendInitiateCreateEvent();
    history.push(`${config.createPath}/create`);
  };

  const renderContent = () => {
    if (selectedCropSeasonIds.length === 0) {
      return (
        <div className="pt-4 px-4">
          <div className="bg-white shadow-sm-card rounded-lg">
            <EmptyTableState
              title="No Crop Seasons Found"
              message="Select or create a crop season to view Invoices."
              overlayIcon={NoCropSeasonSelectedIcon}
              button={
                <Button
                  type={ButtonType.primary}
                  size="large"
                  onClick={() => history.push(paths.cropSeasons)}
                >
                  Go to Crop Seasons
                </Button>
              }
            />
          </div>
        </div>
      );
    }

    if (!hasCompletedInitialLoad) {
      return (
        <div className="flex items-center justify-center flex-1">
          <LoadingSpinner
            message="Loading..."
            className="flex items-center justify-center w-60 py-8 px-4 rounded-lg bg-white shadow-md"
          />
        </div>
      );
    }

    if (!loading && items.length === 0 && !hasSearchTerm && !hasFilters) {
      return (
        <div className="pt-4 px-4">
          <div className="bg-white shadow-sm-card rounded-lg">
            <EmptyTableState
              title="No Invoices Found"
              message="Create an Invoice or change the selected season."
              overlayIcon={NoInvoicesFoundIcon}
              button={
                <Button
                  type={ButtonType.primary}
                  size="large"
                  icon={<PlusIcon />}
                  onClick={createNewInvoice}
                >
                  {config.createButtonLabel}
                </Button>
              }
            />
          </div>
        </div>
      );
    }

    return (
      <InvoiceListView
        invoices={items}
        totalElements={hasSelectedCropSeasons ? totalElements : 0}
        currentPage={currentPage}
        pageSize={pageSize}
        isLoading={loading}
        onItemDeleted={loadInvoices}
        config={{
          icon: config.icon,
          title: config.title,
          searchPlaceholder: config.searchPlaceholder,
          createButtonLabel: config.createButtonLabel,
          createPath: config.createPath,
          sortOptions: config.sortOptions
        }}
      />
    );
  };

  return buildLandingPage(renderContent(), {
    breadcrumbItems: [
      {
        title: config.breadcrumbTitle,
        value: config.breadcrumbValue,
        isLast: true
      }
    ],
    // Parity with the Applications / Work Orders landing pages: multiple
    // active seasons may be selected together, but a past/upcoming season is
    // exclusive. Mixing the three groups stays a create-flow capability.
    allowMultipleCropSeasonSelection: false,
    onCropSeasonSelect: forceRefetch
  });
};

/**
 * Entry composition for the redesigned Invoices module: wires the URL-synced
 * summary-params and per-module sort providers around the page wrapper.
 */
const InvoicesPage: React.FC = () => {
  useDestroyRecordSourceMessageOnNavigation({ pathname: paths.invoices });
  useDismissNotificationOnNavigation(
    paths.invoices,
    dismissInvoiceNotification
  );

  return (
    <SummaryParamsProvider>
      <OperationSortProvider
        storageKey={InvoicesConfig.storageKey}
        defaultSort={INVOICE_DEFAULT_SORT}
      >
        <InvoicesPageWrapper
          config={InvoicesConfig}
          useSummaryHook={useInvoicesSummary}
        />
      </OperationSortProvider>
    </SummaryParamsProvider>
  );
};

export default InvoicesPage;
